  "use client"

  import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog"
  import { Badge } from "../ui/badge"
  import { Textarea } from "../ui/textarea"
  import { Button } from "../ui/button"
  import { CheckCircle, XCircle, Clock } from "lucide-react"
  import { format } from "date-fns"
  import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
  } from "../ui/table"

  interface LeaveReviewModalEmpProps {
    isOpen: boolean
    onClose: () => void
    leaveRequest: any // Ideally define a proper type
    isViewOnly?: boolean
  }

  export function LeaveReviewModalEmp({
    isOpen,
    onClose,
    leaveRequest,
    isViewOnly = false
  }: LeaveReviewModalEmpProps) {

    // Calculate total leave days from leaveDayDetails
    const totalDays = leaveRequest?.leaveDayDetails?.reduce((sum: number, d: any) => {
      if (!d.dayType) return sum;
      const dayType = d.dayType.toLowerCase();
      if (dayType === "full day") return sum + 1;
      if (dayType === "first-half" || dayType === "second-half") return sum + 0.5;
      return sum;
    }, 0) || 0

    // Helper for status badge
    const getApprovalBadge = (status: string) => {
      switch (status.toLowerCase()) {
        case "approved":
          return <Badge className="bg-green-100 text-green-700 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Approved</Badge>
        case "pending":
          return <Badge className="bg-orange-100 text-orange-700 flex items-center gap-1"><Clock className="h-3 w-3" /> Pending</Badge>
        case "rejected":
          return <Badge className="bg-red-100 text-red-700 flex items-center gap-1"><XCircle className="h-3 w-3" /> Rejected</Badge>
        case "pending_manager_approval":
          return <Badge className="bg-orange-100 ..."><Clock /> Pending Manager Approval</Badge>
        case "pending_hr_approval":
          return (
            <Badge className="bg-blue-100 text-blue-700 flex items-center gap-1">
              <Clock className="h-3 w-3" /> Pending HR Approval
            </Badge>
          )
        default:
          return <Badge variant="outline">{status}</Badge>
      }
    }

    // Helper to format date
    const formatDate = (dateString: string | null | undefined) => {
      if (!dateString) return "--";
      try {
        const date = new Date(dateString);
        return format(date, "dd-MM-yyyy");
      } catch {
        return "--";
      }
    };

    // Helper to format date with time
    const formatDateTime = (dateString: string | null | undefined) => {
      if (!dateString) return "--";
      try {
        const date = new Date(dateString);
        return format(date, "dd-MM-yyyy HH:mm");
      } catch {
        return "--";
      }
    };

    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-6xl max-h-[90vh] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
          <DialogHeader className="border-b border-gray-200 pb-4 px-6 pt-6 flex flex-row items-center justify-between">
            <div className="flex items-center gap-3">
              <DialogTitle className="text-xl font-bold text-gray-900">
                Leave Details
              </DialogTitle>
              <Badge className="text-orange-600 border-orange-200 bg-orange-50 text-sm font-medium">
                {leaveRequest?.type || "Leave"}
              </Badge>
            </div>
          </DialogHeader>

          <div className="space-y-6 py-4 px-6">
            {/* Applied On and Duration in same line */}
            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600">
              <div>
                <strong className="text-gray-900">Applied On:</strong>{" "}
                {leaveRequest?.appliedDate ? leaveRequest?.appliedDate : "-"}
              </div>
              <div>
                <strong className="text-gray-900">Duration:</strong>{" "}
                <span className="text-gray-700">
                  {totalDays} {totalDays === 1 ? "Day" : "Days"} ({leaveRequest?.startDate ? formatDate(leaveRequest.startDate) : "-"} - {leaveRequest?.endDate ? formatDate(leaveRequest.endDate) : "-"})
                </span>
              </div>
            </div>

            {/* If leave is cancelled, show a single info box */}
            {leaveRequest?.isCancelled ? (
              <div className="border border-gray-200 rounded-lg p-4 bg-yellow-50 shadow-sm">
                <div className="font-medium text-gray-900 text-base">
                  User has already cancelled their leave
                </div>
              </div>
            ) : (
              <>
                {/* Manager Approval and HR Approval in horizontal layout */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Manager Approval */}
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 shadow-sm">
                    <div className="font-medium text-gray-900 text-base flex items-center gap-2 mb-3">
                      Manager Approval {getApprovalBadge(leaveRequest?.managerResponse || "pending_manager_approval")}
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="text-gray-600">
                        <strong className="text-gray-900">Reviewed By:</strong>{" "}
                        <span className="text-gray-700">
                          {leaveRequest?.managerName || "--"}
                        </span>
                      </div>
                      <div className="text-gray-600">
                        <strong className="text-gray-900">Reviewed On:</strong>{" "}
                        <span className="text-gray-700">
                          {formatDateTime(leaveRequest?.managerResponseOn)}
                        </span>
                      </div>
                      {leaveRequest?.managerComment &&
                        leaveRequest.managerComment.trim() !== "" &&
                        leaveRequest.managerResponse?.toLowerCase() !== "pending" && (
                          <div className="text-gray-600 pt-2 border-t border-gray-200">
                            <strong className="text-gray-900">Comment:</strong>{" "}
                            <span className="text-gray-700 break-all whitespace-pre-wrap">
                              {leaveRequest.managerComment}
                            </span>
                          </div>
                        )}
                    </div>
                  </div>

                  {/* HR Approval */}
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 shadow-sm">
                    <div className="font-medium text-gray-900 text-base flex items-center gap-2 mb-3">
                      HR Approval {getApprovalBadge(leaveRequest?.hrResponse || "Pending")}
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="text-gray-600">
                        <strong className="text-gray-900">Reviewed By:</strong>{" "}
                        <span className="text-gray-700">
                          {leaveRequest?.hrName || "--"}
                        </span>
                      </div>
                      <div className="text-gray-600">
                        <strong className="text-gray-900">Reviewed On:</strong>{" "}
                        <span className="text-gray-700">
                          {formatDateTime(leaveRequest?.hrResponseOn)}
                        </span>
                      </div>
                      {leaveRequest?.hrComment &&
                        leaveRequest.hrComment.trim() !== "" &&
                        leaveRequest.hrResponse?.toLowerCase() !== "pending" && (
                          <div className="text-gray-600 pt-2 border-t border-gray-200">
                            <strong className="text-gray-900">Comment:</strong>{" "}
                            <span className="text-gray-700 break-all whitespace-pre-wrap">
                              {leaveRequest.hrComment}
                            </span>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Reason and Leave Day Details in horizontal layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Reason */}
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 shadow-sm">
                <div className="font-medium text-gray-900 text-base mb-2">
                  Reason
                </div>
                <div className="text-sm text-gray-700 break-all whitespace-pre-wrap">
                  {leaveRequest?.reason || "-"}
                </div>
              </div>

              {/* Leave Day Details */}
              {leaveRequest?.leaveDayDetails && leaveRequest.leaveDayDetails.length > 0 ? (
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 shadow-sm">
                  <div className="font-medium text-gray-900 text-base mb-4">
                    Leave Day Details
                  </div>
                  <div className="overflow-x-auto">
                    <Table>
                      {/* <TableHeader>
                        <TableRow className="bg-white">
                          <TableHead className="font-semibold text-gray-700">Date</TableHead>
                          <TableHead className="font-semibold text-gray-700">Day Type</TableHead>
                        </TableRow>
                      </TableHeader> */}
                      <TableBody>
                        {leaveRequest.leaveDayDetails.map((dayDetail: any, index: number) => {
                          // Format dayType for display
                          const formatDayType = (dayType: string) => {
                            if (!dayType) return "Full Day";
                            const lower = dayType.toLowerCase();
                            if (lower === "first-half") return "First-Half";
                            if (lower === "second-half") return "Second-Half";
                            return "Full Day";
                          };
                          
                          return (
                            <TableRow key={index} className="hover:bg-gray-100">
                              <TableCell className="text-sm text-gray-700">
                                {dayDetail.leaveDate ? formatDate(dayDetail.leaveDate) : "--"}
                              </TableCell>
                              <TableCell className="text-sm text-gray-700">
                                {formatDayType(dayDetail.dayType)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 shadow-sm">
                  <div className="font-medium text-gray-900 text-base mb-2">
                    Leave Day Details
                  </div>
                  <div className="text-sm text-gray-600">No leave day details available</div>
                </div>
              )}
            </div>

            {/* Comments (optional) */}
            {leaveRequest?.comments && (
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Comments</label>
                <Textarea
                  value={leaveRequest.comments}
                  readOnly
                  className="min-h-[100px] bg-white border-gray-200 text-gray-900"
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    )
  }
